// Subscription state surfaced in the app shell.
//  - Trial ≤7 days left ........ amber banner (dismissible for the day)
//  - Trial expired (in grace) .. red banner, persistent
//  - Payment past due .......... amber banner, persistent
//  - LOCKED, admin ............. red banner (they can still reach Subscription)
//  - LOCKED, non-admin ......... full-screen overlay (their API calls 402 anyway)
// Backend enforcement lives in dmsUserAuth.middleware; this is the honest UX
// for it. Fails silent: if /api/stripe/status is unreachable, nothing renders.
import React, { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { Link } from 'react-router-dom';

interface EntitlementStatus {
  status: string;
  trialDaysLeft: number | null;
  graceEndsAt: string | null;
  locked: boolean;
  lockReason: string | null;
  legacyUnlimited?: boolean;
}

const SUBSCRIBE_PATH = '/admin/settings?tab=subscription';
const dismissKey = () => `lex-trial-banner-dismissed-${new Date().toISOString().slice(0, 10)}`;

export default function EntitlementBanner() {
  const { keycloak } = useKeycloak();
  const [ent, setEnt] = useState<EntitlementStatus | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey()) === '1'; } catch { return false; }
  });

  const roles: string[] = (keycloak?.tokenParsed as any)?.realm_access?.roles || [];
  const isAdmin = roles.includes('admin') || roles.includes('system-admin');

  useEffect(() => {
    if (!keycloak?.token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/stripe/status', {
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setEnt(d);
      } catch {
        /* fail silent — billing UX must never break the app */
      }
    })();
    return () => { cancelled = true; };
  }, [keycloak?.token]);

  if (!ent || ent.legacyUnlimited) return null;

  const fmt = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : '';

  // ---- hard lock for non-admins: the overlay owns the screen ----
  if (ent.locked && !isAdmin) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gray-900/95 p-6">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl dark:bg-gray-800">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-2xl dark:bg-red-900/40">🔒</div>
          <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-gray-100">Subscription required</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            Your firm's EdgeLex subscription has lapsed, so access is paused. Nothing has been deleted — every matter,
            document, and message is exactly where you left it.
          </p>
          <p className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-100">
            Ask your firm administrator to reactivate in Firm Settings → Subscription.
          </p>
        </div>
      </div>
    );
  }

  // ---- banners ----
  let tone: 'amber' | 'red' | null = null;
  let text: React.ReactNode = null;
  let dismissible = false;

  if (ent.locked && isAdmin) {
    tone = 'red';
    text = <>Your firm's access is paused for your team — reactivate to restore everyone. Your admin access remains so you can subscribe.</>;
  } else if (ent.status === 'expired') {
    tone = 'red';
    text = <>Your trial has ended. Full access continues until <b>{fmt(ent.graceEndsAt)}</b> — subscribe to keep your firm running.</>;
  } else if (ent.status === 'past_due') {
    tone = 'amber';
    text = <>Your last payment didn't go through — update your card to keep everything running.</>;
  } else if (ent.status === 'trialing' && ent.trialDaysLeft !== null && ent.trialDaysLeft <= 7) {
    tone = 'amber';
    dismissible = true;
    text = <><b>{ent.trialDaysLeft} day{ent.trialDaysLeft === 1 ? '' : 's'}</b> left in your free trial.</>;
  }

  if (!tone || (dismissible && dismissed)) return null;

  return (
    <div
      className={
        'flex items-center gap-3 px-4 py-2 text-sm ' +
        (tone === 'red'
          ? 'bg-red-600 text-white'
          : 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100')
      }
    >
      <span className="min-w-0 flex-1 truncate">{text}</span>
      {isAdmin ? (
        <Link
          to={SUBSCRIBE_PATH}
          className={
            'shrink-0 rounded-md px-3 py-1 text-xs font-semibold ' +
            (tone === 'red' ? 'bg-white text-red-700' : 'bg-amber-600 text-white')
          }
        >
          {ent.status === 'past_due' ? 'Update payment' : 'Subscribe'}
        </Link>
      ) : (
        <span className="shrink-0 text-xs opacity-80">Ask your administrator</span>
      )}
      {dismissible && (
        <button
          onClick={() => {
            try { localStorage.setItem(dismissKey(), '1'); } catch {}
            setDismissed(true);
          }}
          aria-label="Dismiss for today"
          className="shrink-0 text-xs opacity-70 hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  );
}
